import { acceptHMRUpdate, defineStore } from 'pinia'
import { UserProfile, RegisterCredentials, UserCredentials } from "../types";
import type { AxiosError, AxiosInstance } from 'axios'
import { API } from '../services';
import { getAuth, onAuthStateChanged, updatePassword as firebaseUpdatePassword, User as FirebaseUser, signInWithEmailAndPassword, updateProfile, EmailAuthProvider, reauthenticateWithCredential, sendEmailVerification, updateEmail, verifyBeforeUpdateEmail, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { errorTracker } from '../services/error/errorTracking';

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $axios: AxiosInstance
    catTags: string[]
  }
}

interface UserState {
  userProfile: UserProfile | null;
  isAuthenticated: boolean;
  subscriptionData: SubscriptionData | null;
  isInitialized: boolean;
  isLoading: boolean;
  isLoggingIn: boolean;
  isRegistering: boolean;
  isSubscriptionLoaded: boolean;
  _subscriptionFetchPromise?: Promise<any> | null;
  impersonationEmail: string | null;
}

interface SubscriptionData {
  subscriptionType?: string | null;
  subscriptionStatus: string;
  isFreeUser?: boolean;
  subscriptionWillCancel?: boolean;
  subscriptionPeriodEnd?: string | null;
  trialEndDate?: string | null;
  billingInterval?: string | null;
}

const useUserStore = defineStore('user', {
  state: (): UserState => ({
    userProfile: null,
    isAuthenticated: false,
    subscriptionData: null,
    isInitialized: false,
    isLoading: false,
    isLoggingIn: false,
    isRegistering: false,
    isSubscriptionLoaded: false,
    _subscriptionFetchPromise: null,
    impersonationEmail: null
  }),
  getters: {
    isImpersonating(state): boolean {
      return !!state.impersonationEmail;
    },
    /**
     * Returns the Firebase UID to use for API calls.
     * During impersonation, the profile is already the impersonated user's,
     * so this always returns the correct UID.
     */
    effectiveFirebaseUid(state): string | null {
      return state.userProfile?.firebaseUserUuid ?? null;
    },
    isUserAuthenticated(state): boolean {
      return state.isAuthenticated;
    },
    initializationComplete: (state) => {
      return state.isInitialized;
    },
    userInitial: (state) => state.userProfile?.name ? state.userProfile.name.charAt(0).toUpperCase() : '?',
    userId: (state) => state.userProfile?.id,
    isFreemiumUser: (state) => {
      if (state.subscriptionData?.isFreeUser != null) {
        return state.subscriptionData.isFreeUser === true;
      }

      const status = state.userProfile?.subscriptionStatus;
      return status === 'inactive' || status === 'canceled';
    },
    isCanceled: (state) => {
      return state.userProfile?.subscriptionStatus === 'canceled';
    },
    isSubscriptionWillCancel: (state) => {
      return state.subscriptionData?.subscriptionWillCancel === true;
    },
    subscriptionPeriodEnd: (state) => {
      return state.subscriptionData?.subscriptionPeriodEnd || null;
    },
    hasAnyAccess: (state) => {
      const subscriptionStatus =
        state.subscriptionData?.subscriptionStatus ?? state.userProfile?.subscriptionStatus;
      const isActiveOrTrial = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
      const isFreemiumUser =
        state.subscriptionData?.isFreeUser === true ||
        subscriptionStatus === 'inactive' ||
        subscriptionStatus === 'canceled';
      const isNonAccessible = subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid';
      return !isNonAccessible && (isActiveOrTrial || isFreemiumUser);
    },
    isGoogleSignIn: (): boolean => {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return false;
      }

      return user.providerData.some(
        (provider) => provider.providerId === 'google.com'
      );
    }
  },
  actions: {
    async initializeAuth() {
      // Load impersonation email from localStorage
      const storedImpersonationEmail = localStorage.getItem('impersonationEmail');
      if (storedImpersonationEmail) {
        this.impersonationEmail = storedImpersonationEmail;
      }

      const auth = getAuth();
      this.isLoading = true;

      try {
        const storedProfile = localStorage.getItem('userProfile');
        if (storedProfile) {
          this.userProfile = JSON.parse(storedProfile);
        }

        return new Promise((resolve) => {
          onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
              this.isAuthenticated = true;

              if (this.impersonationEmail) {
                // During impersonation, use /users/me which returns the
                // impersonated user's profile via the backend middleware
                await this.fetchCurrentUserProfile();
              } else {
                await this.fetchUserProfile(firebaseUser.uid);
              }

              // Sync email from Firebase auth to backend profile
              // (skip during impersonation — the profile email belongs to the impersonated user)
              if (!this.impersonationEmail && this.userProfile && this.userProfile.email !== firebaseUser.email) {
                const response = await API.users.updateEmail(firebaseUser.uid, firebaseUser.email || '');
                if (response.status === 200) {
                  this.userProfile.email = firebaseUser.email || '';
                  localStorage.setItem('userProfile', JSON.stringify(this.userProfile));
                } else {
                  console.error('[UserStore] onAuthStateChanged: Failed to update email in backend.');
                  throw new Error("Failed to update email in backend for user " + firebaseUser.uid);
                }
              }
            } else {
              this.isAuthenticated = false;
              this.userProfile = null;
              localStorage.removeItem('userProfile');
            }
            this.isInitialized = true;
            this.isLoading = false;
            resolve(firebaseUser);
          });
        });
      } catch (error) {
        console.error('[UserStore] initializeAuth: Error:', error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'initializeAuth'
        });
        this.isLoading = false;
        this.isInitialized = true;
        console.error('[UserStore] initializeAuth: Error caught. isLoading set to false, isInitialized set to true.');
        throw error;
      }
    },
    async hasValidSubscription(): Promise<boolean> {
      try {
        const subscriptionStatusFromState = this.userProfile?.subscriptionStatus;
        // Optimistic check based on local state first
        if (subscriptionStatusFromState === 'active' || subscriptionStatusFromState === 'trialing') {
          return true;
        }
        // If explicitly canceled in local state, and we trust local state here, return false.
        // However, an API check is more definitive.
        // if (subscriptionStatusFromState === 'canceled') {
        //     return false;
        // }

        const user = getAuth().currentUser;
        // During impersonation, use the profile's UID (which is the impersonated user's)
        const firebaseUuid = this.userProfile?.firebaseUserUuid || user?.uid;

        if (!user || !firebaseUuid || !user.emailVerified) {
          // console.warn('hasValidSubscription: User not available, not verified, or UUID missing.');
          return false;
        }

        const response = await API.users.checkSubscriptionStatus(firebaseUuid);
        if (response.status === 200 && response.data) {
          const newStatus = response.data.subscriptionStatus;
          if (this.userProfile) {
            this.userProfile.subscriptionStatus = newStatus; // Update local profile
          }
          return newStatus === 'active' || newStatus === 'trialing';
        }
        // If API call fails or returns non-200, assume no valid subscription but don't logout.
        return false;
      } catch (error) {
        console.error('hasValidSubscription: Error checking subscription status:', error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          // Corrected action name
          action: 'hasValidSubscription'
        });
        // Do NOT call this.logout() here.
        return false; // Assume no valid subscription on error
      }
    },
    async isSubscriptionCancelled(): Promise<boolean> {
      try {
        const subscriptionStatusFromState = this.userProfile?.subscriptionStatus;
        // Optimistic check based on local state first
        if (subscriptionStatusFromState === 'canceled') {
          return true;
        }

        const user = getAuth().currentUser;
        // During impersonation, use the profile's UID (which is the impersonated user's)
        const firebaseUuid = this.userProfile?.firebaseUserUuid || user?.uid;

        if (!user || !firebaseUuid || !user.emailVerified) {
          // console.warn('isSubscriptionCancelled: User not available, not verified, or UUID missing.');
          return false; // Or perhaps true, if no user means it's effectively cancelled for access purposes?
          // Returning false seems safer (i.e., not actively cancelled if unknown)
        }

        const response = await API.users.checkSubscriptionStatus(firebaseUuid);
        if (response.status === 200 && response.data) {
          const newStatus = response.data.subscriptionStatus;
          if (this.userProfile) {
            this.userProfile.subscriptionStatus = newStatus; // Update local profile
          }
          return newStatus === 'canceled';
        }
        // If API call fails or returns non-200, we can't confirm cancellation.
        // Depending on desired behavior, return false (not definitively cancelled) or handle error.
        // console.warn(`isSubscriptionCancelled: API call failed or returned non-200. Status: ${response?.status}. Assuming not actively cancelled.`);
        return false;
      } catch (error) {
        console.error('isSubscriptionCancelled: Error checking subscription status:', error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'isSubscriptionCancelled'
        });
        // Do NOT call this.logout() here.
        return false; // Assume not actively cancelled on error
      }
    },
    setUserProfile(user: UserProfile) {
      this.userProfile = user;
      localStorage.setItem('userProfile', JSON.stringify(user));
      // Note: preferredLanguage is NOT applied here on purpose.
      // The URL is always the source of truth for i18n locale.
      // A URL without a locale prefix (e.g. /app/recommendations) means English (default).
      // The user's preferredLanguage is persisted to the backend for server-side use
      // (e.g. emails) and is applied only when the user actively switches language
      // via the LanguageSwitcher, which updates the URL accordingly.
    },
    async updatePreferredLanguage(locale: string) {
      try {
        const auth = getAuth();
        const firebaseUuid = auth.currentUser?.uid;

        if (!firebaseUuid) {
          return {
            success: false,
            message: 'User not authenticated'
          };
        }

        // Skip if language hasn't changed
        if (this.userProfile?.preferredLanguage === locale) {
          return {
            success: true,
            message: 'Language already set to this value'
          };
        }

        // Call backend API to update preferred language
        const response = await API.users.updatePreferredLanguage(firebaseUuid, locale);

        if (response.success) {
          // Update local profile cache
          if (this.userProfile) {
            this.userProfile.preferredLanguage = locale;
            localStorage.setItem('userProfile', JSON.stringify(this.userProfile));
          }

          // Update i18n locale (this also updates localStorage 'user-locale')
          const { setI18nLanguage } = await import('../i18n/index.js');
          await setI18nLanguage(locale);

          return {
            success: true,
            message: response.content?.message || 'Preferred language updated successfully'
          };
        }

        // Return error message from backend if available
        return {
          success: false,
          message: (response as any).message || 'Failed to update preferred language'
        };
      } catch (error) {
        console.error('[UserStore] updatePreferredLanguage: Error:', error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'updatePreferredLanguage'
        });
        return {
          success: false,
          message: 'Error updating preferred language'
        };
      }
    },
    async subscribeUser(email: string) {
      try {
        await API.users.subscribeEmail(email);
        return {
          success: true
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'subscribeUser'
        });
        return {
          success: false
        };
      }
    },
    async login(credentials: UserCredentials) {
      this.isLoggingIn = true;
      try {
        const auth = getAuth();
        const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);

        const firebaseUser = userCredential.user;

        if (!firebaseUser.emailVerified) {
          this.isLoggingIn = false;
          return {
            success: false,
            reason: "email-not-verified"
          };
        }

        const token = await firebaseUser.getIdToken();
        const firebaseUuid = firebaseUser.uid;

        this.isAuthenticated = true;

        await this.fetchUserProfile(firebaseUuid);

        localStorage.setItem('token', token);
        localStorage.setItem('firebaseUuid', firebaseUuid);

        // Keep isLoggingIn true to maintain loading state through redirect
        return {
          success: true
        };
      } catch (error) {
        console.error('[UserStore] login: Error:', error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'login'
        });
        // Clear loading state immediately on error
        this.isLoggingIn = false;
        if (error instanceof FirebaseError) {
          const errorCode = error.code;
          switch (errorCode) {
            case 'auth/invalid-credential':
            case 'auth/invalid-email':
            case 'auth/wrong-password':
              return {
                success: false,
                reason: "invalid-credential"
              };
            case 'auth/too-many-requests':
              return {
                success: false,
                reason: "too-many-attemps"
              };
            default:
              return {
                success: false,
                reason: "firebase-error"
              };
          }
        }
        return {
          success: false,
          reason: "unknown-error"
        };
      }
    },
    async loginWithGoogle(recaptchaToken?: string) {
      this.isLoggingIn = true;
      try {
        const auth = getAuth();
        const provider = new GoogleAuthProvider();

        // Optional: Add scopes if needed
        provider.addScope('email');
        provider.addScope('profile');

        const result = await signInWithPopup(auth, provider);
        const firebaseUser = result.user;

        // Google users are automatically email verified
        const token = await firebaseUser.getIdToken();
        const firebaseUuid = firebaseUser.uid;

        this.isAuthenticated = true;

        // Try to fetch existing profile first
        try {
          await this.fetchUserProfile(firebaseUuid);

          // If profile doesn't exist, create new user
          if (!this.userProfile) {
            const displayName = firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Google User';
            const email = firebaseUser.email || '';

            // Get current locale from i18n instance or localStorage or use default
            const { i18n, DEFAULT_LOCALE, getSavedLocale } = await import('../i18n/index.js');
            const currentLocale = i18n.global.locale.value || getSavedLocale() || DEFAULT_LOCALE;

            // Create user in backend database
            const createUserResponse = await API.users.registerGoogleUser({
              name: displayName,
              email: email,
              firebaseUserUuid: firebaseUuid,
              preferredLanguage: currentLocale
            });

            if (createUserResponse.status === 200 || createUserResponse.status === 201) {
              // Fetch the newly created profile
              await this.fetchUserProfile(firebaseUuid);
            } else {
              throw new Error('Failed to create user profile in backend');
            }
          }
        } catch (profileError) {
          console.error('[UserStore] loginWithGoogle: Error handling user profile:', profileError);
          throw profileError;
        }

        localStorage.setItem('token', token);
        localStorage.setItem('firebaseUuid', firebaseUuid);

        // Keep isLoggingIn true to maintain loading state through redirect
        return {
          success: true
        };

      } catch (error) {
        console.error('[UserStore] loginWithGoogle: Error:', error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'loginWithGoogle'
        });

        // Clear loading state immediately on error
        this.isLoggingIn = false;

        if (error instanceof FirebaseError) {
          const errorCode = error.code;
          switch (errorCode) {
            case 'auth/popup-closed-by-user':
              return {
                success: false,
                reason: "popup-closed"
              };
            case 'auth/popup-blocked':
              return {
                success: false,
                reason: "popup-blocked"
              };
            case 'auth/cancelled-popup-request':
              return {
                success: false,
                reason: "popup-cancelled"
              };
            case 'auth/network-request-failed':
              return {
                success: false,
                reason: "network-error"
              };
            default:
              return {
                success: false,
                reason: "firebase-error"
              };
          }
        }

        return {
          success: false,
          reason: "unknown-error"
        };
      }
    },
    async updatePasswordWithCredentials(currentPassword: string, newPassword: string) {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user || !user.email) {
          throw new Error('User is not authenticated or email is missing.');
        }

        // Re-authenticate the user
        const credential = EmailAuthProvider.credential(
          user.email,
          currentPassword
        );
        await reauthenticateWithCredential(user, credential);

        // Update the password
        await firebaseUpdatePassword(user, newPassword);

        // Optionally, force token refresh
        await user.getIdToken(true);

        // Provide feedback
        return {
          success: true,
          message: 'Password updated successfully.',
        };
      } catch (error: any) {
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'updatePasswordWithCredentials'
        });
        if (error.code === 'auth/wrong-password') {
          throw new Error('Incorrect current password.');
        } else if (error.code === 'auth/weak-password') {
          throw new Error('The new password is too weak.');
        } else {
          throw new Error(error.message || 'An error occurred while updating the password.');
        }
      }
    },
    async register(credentials: RegisterCredentials): Promise<{ success: boolean, status?: number, alreadyRegistered?: boolean, errorMessage?: string, errorType?: string }> {
      this.isRegistering = true;
      try {
        console.log('[UserStore] register called with credentials:', {
          name: credentials.name,
          email: credentials.email,
          hasPassword: !!credentials.password,
          hasRecaptchaToken: !!credentials.recaptchaToken,
          recaptchaTokenLength: credentials.recaptchaToken?.length || 0,
          recaptchaTokenPreview: credentials.recaptchaToken ? `${credentials.recaptchaToken.substring(0, 20)}...` : 'undefined',
          hasPreferredLanguage: !!credentials.preferredLanguage
        });

        // Get current locale from i18n instance or localStorage or use default
        const { i18n, DEFAULT_LOCALE, getSavedLocale } = await import('../i18n/index.js');
        const currentLocale = i18n.global.locale.value || getSavedLocale() || DEFAULT_LOCALE;

        // Add preferred language to credentials
        const credentialsWithLanguage = {
          ...credentials,
          preferredLanguage: currentLocale
        };

        console.log('[UserStore] Prepared credentials for API call:', {
          name: credentialsWithLanguage.name,
          email: credentialsWithLanguage.email,
          hasPassword: !!credentialsWithLanguage.password,
          hasRecaptchaToken: !!credentialsWithLanguage.recaptchaToken,
          recaptchaTokenLength: credentialsWithLanguage.recaptchaToken?.length || 0,
          recaptchaTokenPreview: credentialsWithLanguage.recaptchaToken ? `${credentialsWithLanguage.recaptchaToken.substring(0, 20)}...` : 'undefined',
          preferredLanguage: credentialsWithLanguage.preferredLanguage,
          allKeys: Object.keys(credentialsWithLanguage)
        });

        const response = await API.users.register(credentialsWithLanguage);

        if (response?.code === "auth/email-already-in-use") {
          this.isRegistering = false;
          return {
            success: true,
            alreadyRegistered: true
          };
        }

        const success = response.status === 200;

        if (!success) {
          this.isRegistering = false;

          // Extract error message and determine error type for reCAPTCHA errors
          const errorMessage = response.message || 'Registration failed';
          let errorType: string | undefined;

          // Check for reCAPTCHA-related errors
          if (response.status === 400 || response.status === 403) {
            if (errorMessage.includes('reCAPTCHA') || errorMessage.includes('recaptcha')) {
              if (errorMessage.includes('required') || errorMessage.includes('missing')) {
                errorType = 'recaptcha_missing';
              } else if (errorMessage.includes('verification failed') || errorMessage.includes('invalid')) {
                errorType = 'recaptcha_verification_failed';
              } else if (errorMessage.includes('suspicious') || errorMessage.includes('blocked')) {
                errorType = 'recaptcha_suspicious';
              } else {
                errorType = 'recaptcha_error';
              }
            }
          }

          return {
            success: false,
            status: response.status,
            errorMessage,
            errorType
          };
        }
        // Keep isRegistering true on success to maintain loading state through redirect

        return {
          success,
          alreadyRegistered: false
        };

      } catch (error) {
        console.error("[UserStore] register: Error during registration:", error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'register'
        });
        // Clear loading state immediately on error
        this.isRegistering = false;
        const _error = error as AxiosError<{ message?: string }>;
        const errorMessage = _error.response?.data?.message || 'An unexpected error occurred';
        return {
          success: false,
          status: _error.response?.status,
          errorMessage
        };
      }
    },
    async logout() {
      try {
        const auth = getAuth();
        await signOut(auth);
        this.userProfile = null;
        this.isAuthenticated = false;
        this.subscriptionData = null;
        this.isSubscriptionLoaded = false;
        this._subscriptionFetchPromise = null;
        this.impersonationEmail = null;

        // Clear all localStorage items
        localStorage.removeItem('userProfile');
        localStorage.removeItem('token');
        localStorage.removeItem('firebaseUuid');
        localStorage.removeItem('blog');
        localStorage.removeItem('impersonationEmail');

        // Clear business store state to prevent stale data issues
        const { default: useBusinessStore } = await import('./useBusiness');
        const businessStore = useBusinessStore();
        businessStore.clearState();

        return true;
      } catch (error) {
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'logout'
        });
        throw error;
      }
    },
    async resetPassword(email: string) {
      try {
        const response = await API.users.resetPassword(email);
        const success = response.status === 200;
        return {
          success: success
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'resetPassword'
        });
        const _error = error as AxiosError<string>;
        return {
          success: false,
          status: _error.response?.status
        };
      }
    },
    async needsConfirmation() {
      try {
        const result = await API.users.needsConfirmation();
        return result;
      } catch (error) {
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'needsConfirmation'
        });
        return true;
      }
    },
    async resendConfirmationEmail() {
      try {
        const response = await API.users.resendConfirmationEmail();
        const success = response.status === 200;
        return {
          success: success
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'resendConfirmationEmail'
        });
        const _error = error as AxiosError<string>;
        return {
          success: false,
          status: _error.response?.status
        };
      }
    },
    async linkUserToCustomer(stripeSessionId: string) {
      try {
        const response = await API.users.linkUserToCustomer(stripeSessionId);

        if (response.status !== 200) {
          return {
            success: false
          };
        }

        if (!response.data) {
          return {
            success: false
          };
        }

        this.setUserProfile(response.data);

        return {
          success: true
        };
      } catch (error) {
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'linkUserToCustomer'
        });
        const _error = error as AxiosError<string>;
        return {
          success: false,
          status: _error.response?.status
        };
      }
    },
    async fetchUserProfile(firebaseUuid: string | null) {
      try {
        if (!firebaseUuid) {
          const auth = getAuth();
          firebaseUuid = auth.currentUser?.uid ?? null;
          if (firebaseUuid == null) {
            this.userProfile = null;
            this.isAuthenticated = false;
            localStorage.removeItem('userProfile');
            localStorage.removeItem('token');
            localStorage.removeItem('firebaseUuid');
            return;
          }
        }
        const response = await API.users.getUserByFirebaseUuid(firebaseUuid);

        if (response.status === 200 && response.data) {

          this.userProfile = response.data;

          // Note: preferredLanguage is NOT applied here on purpose.
          // The URL is always the source of truth for i18n locale.
          // A URL without a locale prefix (e.g. /app/recommendations) means English (default).
          // Applying preferredLanguage here would override the URL-based locale on hard refresh.

          // Save profile to localStorage
          localStorage.setItem('userProfile', JSON.stringify(this.userProfile));

          const auth = getAuth();
          if (auth.currentUser?.uid === firebaseUuid) {
            this.isAuthenticated = true;
          } else {
            this.isAuthenticated = false;
            this.userProfile = null;
          }
        } else {
          this.userProfile = null;
        }
      } catch (error) {
        console.error(`[UserStore] fetchUserProfile: Error fetching user profile for ${firebaseUuid}:`, error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'fetchUserProfile'
        });
        this.userProfile = null;
      }
    },
    async fetchCurrentUserProfile() {
      try {
        const response = await API.users.getCurrentUser();
        if (response.status === 200 && response.data) {
          this.userProfile = response.data;
          localStorage.setItem('userProfile', JSON.stringify(this.userProfile));
          this.isAuthenticated = true;
        } else {
          this.userProfile = null;
        }
      } catch (error) {
        console.error('[UserStore] fetchCurrentUserProfile: Error:', error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'fetchCurrentUserProfile'
        });
      }
    },
    async updateFullName(newName: string) {
      try {
        const auth = getAuth();

        if (!auth.currentUser) {
          return {
            status: 500,
            content: { message: "Cannot change user name" },
            success: false,
          };
        }

        await updateProfile(auth.currentUser, { displayName: newName });

        // Force a token refresh to get the updated ID token
        const newToken = await auth.currentUser.getIdToken(true);
        localStorage.setItem('token', newToken);

        const response = await API.users.updateName(auth.currentUser.uid, newName);
        if (response.status !== 200) {
          throw new Error("Failed to update profile.");
        }

        // Update the userProfile state
        if (this.userProfile) {
          this.userProfile.name = newName;
          localStorage.setItem('userProfile', JSON.stringify(this.userProfile));
        } else {
          throw new Error("User profile is not available.");
        }
      } catch (error) {
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'updateFullName'
        });
        throw error;
      }
    },
    async updateEmail(newEmail: string, currentPassword: string) {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user || !user.email) {
          throw new Error("User is not authenticated or email is missing.");
        }

        // Re-authenticate the user
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);

        // Send verification email to the new email address
        await verifyBeforeUpdateEmail(user, newEmail);
        return {
          success: true,
          message: "A verification email has been sent to your new email address. Please verify to complete the update.",
        };
      } catch (error: any) {
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'updateEmail'
        });
        if (error.code === 'auth/wrong-password') {
          throw new Error('Incorrect password.');
        } else if (error.code === 'auth/email-already-in-use') {
          throw new Error('The email address is already in use.');
        } else {
          throw error;
        }
      }
    },
    async fetchSubscriptionStatus() {
      if (this._subscriptionFetchPromise) {
        return this._subscriptionFetchPromise;
      }

      if (this.isSubscriptionLoaded && this.subscriptionData) {
        return Promise.resolve(this.subscriptionData);
      }

      this._subscriptionFetchPromise = new Promise(async (resolve, reject) => {
        try {
          const user = getAuth().currentUser;
          const firebaseUuid = this.userProfile?.firebaseUserUuid || user?.uid;
          if (!firebaseUuid) {
            this.isSubscriptionLoaded = false;
            resolve(null);
            return;
          }

          const response = await API.users.checkSubscriptionStatus(firebaseUuid);
          if (response.status === 200 && response.data) {
            this.subscriptionData = response.data;
            if (this.userProfile) {
              this.userProfile.subscriptionStatus = response.data.subscriptionStatus;
              this.userProfile.subscriptionType = response.data.subscriptionType ?? this.userProfile.subscriptionType;
              if (response.data.billingInterval != null) {
                this.userProfile.billingInterval = response.data.billingInterval;
              }
              localStorage.setItem('userProfile', JSON.stringify(this.userProfile));
            }
            this.isSubscriptionLoaded = true;
            resolve(response.data);
          } else {
            this.isSubscriptionLoaded = false;
            reject(new Error('Failed to fetch subscription data'));
          }
        } catch (error) {
          this.isSubscriptionLoaded = false;
          errorTracker.captureError(error as Error, {
            componentName: 'UserStore',
            action: 'fetchSubscriptionStatus'
          });
          reject(error);
        } finally {
          this._subscriptionFetchPromise = null;
        }
      });

      return this._subscriptionFetchPromise;
    },
    async refreshSubscriptionStatus() {
      this._subscriptionFetchPromise = null;
      this.isSubscriptionLoaded = false;

      this._subscriptionFetchPromise = new Promise(async (resolve, reject) => {
        try {
          const user = getAuth().currentUser;
          const firebaseUuid = this.userProfile?.firebaseUserUuid || user?.uid;
          if (!firebaseUuid) {
            this.isSubscriptionLoaded = false;
            resolve(null);
            return;
          }

          const response = await API.users.checkSubscriptionStatus(firebaseUuid);
          if (response.status === 200 && response.data) {
            this.subscriptionData = response.data;
            if (this.userProfile) {
              this.userProfile.subscriptionStatus = response.data.subscriptionStatus;
              this.userProfile.subscriptionType = response.data.subscriptionType ?? this.userProfile.subscriptionType;
              if (response.data.billingInterval != null) {
                this.userProfile.billingInterval = response.data.billingInterval;
              }
              localStorage.setItem('userProfile', JSON.stringify(this.userProfile));
            }
            this.isSubscriptionLoaded = true;
            resolve(response.data);
          } else {
            this.isSubscriptionLoaded = false;
            reject(new Error('Failed to refresh subscription data'));
          }
        } catch (error) {
          this.isSubscriptionLoaded = false;
          errorTracker.captureError(error as Error, {
            componentName: 'UserStore',
            action: 'refreshSubscriptionStatus'
          });
          reject(error);
        } finally {
          this._subscriptionFetchPromise = null;
        }
      });

      return this._subscriptionFetchPromise;
    },
    async hasContentAccess(): Promise<boolean> {
      try {
        const hasSubscription = await this.hasValidSubscription();
        if (hasSubscription) {
          return true;
        }

        if (!this.isSubscriptionLoaded) {
          await this.fetchSubscriptionStatus();
        }

        return this.subscriptionData?.isFreeUser === true;
      } catch (error) {
        console.error('hasContentAccess: Error checking content access:', error);
        errorTracker.captureError(error as Error, {
          componentName: 'UserStore',
          action: 'hasContentAccess'
        });
        return false;
      }
    },
    clearAuthLoadingStates() {
      // Method to clear loading states when navigation is complete
      this.isLoggingIn = false;
      this.isRegistering = false;
    },
    async getUpgradeTransitionStatus() {
      try {
        const status = await API.welcomeAnalysis.getUpgradeTransitionStatus()
        return status
      } catch (error) {
        console.error('Failed to get upgrade transition status:', error)
        return null
      }
    },
    startImpersonation(email: string) {
      this.impersonationEmail = email;
      localStorage.setItem('impersonationEmail', email);
      // Force reload to apply interceptor changes and refresh user data
      window.location.reload();
    },

    stopImpersonation() {
      this.impersonationEmail = null;
      localStorage.removeItem('impersonationEmail');
      // Force reload to apply interceptor changes and refresh user data
      window.location.reload();
    },

    async fetchAllUsers() {
      try {
        const response = await API.users.getAllUsers();
        if (response.status === 200) {
          return response.data;
        } else {
          return [];
        }
      } catch (error) {
        console.error('Error fetching all users:', error);
        return [];
      }
    }
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))

export default useUserStore
